import { PlusOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useList } from '@refinedev/core'
import { useQueryClient } from '@tanstack/react-query'
import { App, Breadcrumb, Button, Table } from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import { mutateNoBody } from '@/api/mutateNoBody'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'

import { paymentMethodColumns } from './columns'
import { PaymentMethodModal } from './components/PaymentMethodModal'

type PaymentMethodDto = components['schemas']['PaymentMethodDto']

export default function PaymentMethodsListPage() {
  const { modal, message } = App.useApp()
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<PaymentMethodDto | undefined>()

  const { result, query } = useList<PaymentMethodDto>({
    resource: 'payment-methods',
    pagination: { mode: 'off' },
  })

  const methods = result.data ?? []

  const patchMethod = async (
    record: PaymentMethodDto,
    body: components['schemas']['AdminPaymentMethodPatch'],
    successText: string,
  ) => {
    try {
      await mutateNoBody(
        (parseAs) =>
          client.PATCH('/v2/admin/payment-methods/{provider}/{method_code}', {
            params: {
              path: {
                provider: record.provider,
                method_code: record.methodCode,
              },
            },
            body,
            parseAs,
          }),
        'Failed to update payment method',
      )
      void message.success(successText)
      invalidateAdminCaches(queryClient)
    } catch (error) {
      if (error instanceof Error) void message.error(error.message)
    }
  }

  const deleteMethod = (record: PaymentMethodDto) => {
    void modal.confirm({
      title: 'Delete payment method',
      content: `Remove ${record.provider} · ${record.methodCode} from the catalog?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await mutateNoBody(
          (parseAs) =>
            client.DELETE(
              '/v2/admin/payment-methods/{provider}/{method_code}',
              {
                params: {
                  path: {
                    provider: record.provider,
                    method_code: record.methodCode,
                  },
                },
                parseAs,
              },
            ),
          'Failed to delete payment method',
        )
        void message.success('Payment method deleted')
        invalidateAdminCaches(queryClient)
      },
    })
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: 'Payment Methods' }]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing(undefined)
            setModalOpen(true)
          }}
        >
          Add method
        </Button>
      </div>

      <Table<PaymentMethodDto>
        loading={query.isLoading}
        dataSource={methods}
        rowKey={(r) => `${r.provider}:${r.methodCode}`}
        columns={paymentMethodColumns({
          onToggle: (record, enabled) =>
            patchMethod(
              record,
              { enabled },
              `Method ${enabled ? 'enabled' : 'disabled'}`,
            ),
          onEdit: (record) => {
            setEditing(record)
            setModalOpen(true)
          },
          onDelete: deleteMethod,
        })}
        pagination={false}
        scroll={{ x: 1100 }}
      />

      <PaymentMethodModal
        open={modalOpen}
        method={editing}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
